import React, { useState, useEffect, useContext, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import BTable from "react-bootstrap/Table";
import { useTable } from "react-table";
import { v4 as uuidv4 } from "uuid";
import { Redirect } from "react-router-dom";
import DatePicker from "react-datepicker";
import dateFormat from "dateformat";
import { listProjects } from "../../graphql/queries";
import { createProject } from "../../graphql/mutations";
import { notification, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Context } from "../../Store.js";
import Amplify, { API, graphqlOperation, Storage } from "aws-amplify";
import config from "../../aws-exports";
import "react-datepicker/dist/react-datepicker.css";
import "./style.css";

const {
  aws_user_files_s3_bucket_region: region,
  aws_user_files_s3_bucket: bucket,
} = config;

const NewProjectPage = () => {
  const [state, dispatch] = useContext(Context);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState({
    projectName: "",
    identificationNumber: "",
    startDate: new Date(),
    endDate: new Date(),
    projectType: "",
    abstract: "",
    uploadAbstract: "",
    username: state.user.username,
    lowerCaseName: "",
    isDeleted: false,
    status: "INACTIVE",
  });

  const [redirect, setRedirect] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchProjects = async () => {
    try {
      const projectData = await API.graphql(
        graphqlOperation(listProjects, {
          filter: {
            status: {
              eq: "INACTIVE",
            },
            username: {
              eq: state.user.username,
            },
          },
        })
      );
      const projectList = projectData.data.listProjects.items;
      setProjects(projectList);
    } catch (error) {
      notification.error({
        message: "Error fetching projects!",
        description: error.message,
        placement: "topRight",
        duration: 1.5,
        onClose: () => {
          setRedirect(null);
          setIsLoading(false);
        },
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "projectName",
      },
      {
        Header: "Identification Number",
        accessor: "identificationNumber",
      },
      {
        Header: "Project Type",
        accessor: "projectType",
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        Cell: (props) => (
          <span className="string">
            {dateFormat(props.value, "mmmm dS, yyyy")}
          </span>
        ),
      },
      {
        Header: "End Date",
        accessor: "endDate",
        Cell: (props) => (
          <span className="string">
            {dateFormat(props.value, "mmmm dS, yyyy")}
          </span>
        ),
      },
      {
        header: "",
        id: "click-me-button",
        Cell: (props) => <a className="btn btn-primary">View</a>,
      },
    ],
    []
  );

  const clickProject = async (project) => {
    dispatch({ type: "SET_SELECTED_PROJECT", payload: project });
    setRedirect(`project-detail/${project.id}`);
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const file = e.target.files[0];
    const extension = file.name.split(".")[1];
    const name = file.name.split(".")[0];
    const key = `abstracts/${uuidv4()}${name}.${extension}`;
    const url = `https://${bucket}.s3.${region}.amazonaws.com/public/${key}`;
    try {
      // Upload the file to s3 with public access level.
      await Storage.put(key, file, {
        level: "public",
        contentType: file.type,
      });
      // Retrieve the uploaded file to display
      setProject({ ...project, uploadAbstract: url });
      setIsLoading(false);
    } catch (error) {
      notification.error({
        message: "Error uploading file!",
        description: error.message,
        placement: "topRight",
        duration: 1.0,
        onClose: () => {
          setRedirect(null);
          setIsLoading(false);
        },
      });
    }
  };

  const addProject = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    project.lowerCaseName = project.projectName.toLocaleLowerCase();
    setProject({ lowerCaseName: project.projectName.toLowerCase() });
    try {
      const projectData = await API.graphql(
        graphqlOperation(createProject, { input: project })
      );
      const projectList = [...projects];
      projectList.push(projectData.data.createProject);
      setProjects(projectList);
      notification.success({
        message: "Project Created!",
        description: "Project Successfully Created",
        placement: "topRight",
        duration: 2.0,
        onClose: () => {
          setProject({
            ...project,
            projectName: "",
            identificationNumber: "",
            startDate: new Date(),
            endDate: new Date(),
            projectType: "",
            abstract: "",
            lowerCaseName: "",
            uploadAbstract: "",
          });
          setIsLoading(false);
        },
      });
    } catch (error) {
      notification.error({
        message: "Error adding project!",
        description: error.message,
        placement: "topRight",
        duration: 10.0,
        onClose: () => {
          setRedirect(null);
          setIsLoading(false);
        },
      });
    }
  };

  useEffect(() => {
    (async () => {
      await fetchProjects();
    })();
  }, []);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: projects,
    });

  if (redirect) {
    return <Redirect to={redirect} />;
  } else {
    return (
      <React.Fragment>
        <div
          className="container inner-class-container"
          style={{ height: "100%" }}
        >
          <form>
            <fieldset>
              <legend>Project Form</legend>
              <div className="form-group">
                <label htmlFor="projectName">Project Name</label>
                <input
                  className="form-control"
                  id="projectName"
                  aria-describedby="emailHelp"
                  placeholder="Enter Project Name"
                  onChange={(e) =>
                    setProject({ ...project, projectName: e.target.value })
                  }
                  value={project.projectName}
                />
              </div>

              <div className="form-group">
                <label htmlFor="identificationNumber">
                  Identification Number
                </label>
                <input
                  className="form-control"
                  id="identificationNumber"
                  placeholder="Identification Number"
                  onChange={(e) =>
                    setProject({
                      ...project,
                      identificationNumber: e.target.value,
                    })
                  }
                  value={project.identificationNumber}
                />
              </div>

              <div className="form-group">
                <label htmlFor="startDate">Project Start Date</label>
                <DatePicker
                  className="form-control"
                  selected={project.startDate}
                  onChange={(date) =>
                    setProject({ ...project, startDate: date })
                  }
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">Project End Date</label>
                <DatePicker
                  className="form-control"
                  selected={project.endDate}
                  onChange={(date) => setProject({ ...project, endDate: date })}
                />
              </div>

              <div className="form-group">
                <label htmlFor="projectType">Project Type</label>
                <select
                  className="form-control"
                  id="projectType"
                  value={project.projectType}
                  onChange={(e) =>
                    setProject({ ...project, projectType: e.target.value })
                  }
                >
                  <option value="">------ Select Project Type ------</option>
                  <option value="Journal Publication">
                    Journal Publication
                  </option>
                  <option value="Technical Report">Technical Report</option>
                  <option value="Research Proposals">Research Proposal</option>
                  <option value="Conference Paper">Conference Paper</option>
                  <option value="Seminar Paper">Seminar Paper</option>
                  <option value="Workshop Paper">Workshop Paper</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="abstract">Abstract (Summary)</label>
                <textarea
                  className="form-control"
                  id="abstract"
                  rows="5"
                  onChange={(e) =>
                    setProject({ ...project, abstract: e.target.value })
                  }
                  value={project.abstract}
                ></textarea>
              </div>
              <div className="form-group">
                <label htmlFor="uploadAbstract">Upload Abstract PDF </label>
                <input
                  accept="image/*,.pdf"
                  onChange={(e) => handleImageUpload(e)}
                  type="file"
                  className="form-control-file"
                  id="uploadAbstract"
                  aria-describedby="fileHelp"
                />
              </div>
              <a type="submit" className="btn btn-primary" onClick={addProject}>
                {isLoading ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 24 }} />}
                  />
                ) : (
                  "Submit"
                )}
              </a>
            </fieldset>
          </form>
          <br />
          <BTable striped bordered hover size="sm" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    onClick={() => clickProject(row.original)}
                  >
                    {row.cells.map((cell) => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </BTable>
        </div>
      </React.Fragment>
    );
  }
};

export default NewProjectPage;
